import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import React, { Component } from "react";
import { Check } from "lucide-react";
import { Col, Container, FormGroup, Label, Row } from "reactstrap";
import { DropdownChangeEvent } from "primereact/dropdown";
import { Button } from "primereact/button";
import { SelectItem } from "../../models/SelectItem";
import ExtendedDropdown from "../../components/ExtendedDropdown";
import Loader from "../../components/Loader";
import { UserAddress } from "../../models/User";
import { countryCodeToFlag } from "../../utils/utils";

interface AddUserAddressProps {
    onHide: () => void;
    address: UserAddress;
    countries: SelectItem[];
    onAddressUpdate: (address: UserAddress) => void;
}

interface AddUserAddressState {
    address: UserAddress;
    isLoading: boolean;
}

export default class AddUserAddress extends Component<AddUserAddressProps, AddUserAddressState> {
    validationErrors: Map<string, boolean> = new Map<string, boolean>();
    constructor(props: AddUserAddressProps | Readonly<AddUserAddressProps>) {
        super(props);

        this.state = {
            address: this.props.address,
            isLoading: false,
        };
    }

    updateValue = (fieldName: string, value: string) => {
        const userAddressDetails: any = this.state.address;
        userAddressDetails[fieldName] = value;
        this.setState({ address: userAddressDetails });
    };

    onSave = () => {
        this.props.onAddressUpdate(this.state.address);
    };

    onCountryChange = (e: DropdownChangeEvent) => {
        const addressDetails = this.state.address;
        addressDetails!.countryId = (e as DropdownChangeEvent).value;

        this.setState({ address: addressDetails });
    };

    selectedCountryTemplate(option: SelectItem): React.ReactNode {
        if (option) {
            return (
                <div className="flex items-center gap-2">
                    <span className="text-base leading-none">{countryCodeToFlag(option.value)}</span>
                    <span>{option.label}</span>
                </div>
            );
        }
        return <span></span>;
    }

    countryOptionTemplate(option: SelectItem): React.ReactNode {
        return (
            <div className="flex items-center gap-2">
                <span className="text-base leading-none">{countryCodeToFlag(option.value)}</span>
                <span>{option.label}</span>
            </div>
        );
    }

    render() {
        if (this.state.address == null) {
            return (
                <>
                    <Loader />;
                </>
            );
        }
        return (
            <Dialog header="Address" visible={true} style={{ width: "60vw" }} onHide={this.props.onHide}>
                        <Container fluid>
                            <Row className="d-flex justify-content-center">
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Address Line</Label>
                                        <InputText
                                            value={this.state.address?.addressLine}
                                            className={"form-control"}
                                            type="text"
                                            onChange={(e) => this.updateValue("addressLine", e.target.value)}
                                        />
                                        <small className="text-muted">
                                            Address Line should include only street/unit (e.g., 2345 Anywhere St Apt 12). City/State/ZIP go in their respective fields.
                                        </small>
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Country</Label>
                                        <ExtendedDropdown
                                            style={{ width: "100%" }}
                                            value={this.state.address?.countryId}
                                            options={this.props.countries}
                                            onChange={this.onCountryChange}
                                            filter
                                            filterBy="label"
                                            placeholder="Select a Country"
                                            required={true}
                                            name="country"
                                            onValidStatusChanged={(value: boolean) => this.validationErrors.set("countryId", value)}
                                            valueTemplate={this.selectedCountryTemplate}
                                            itemTemplate={this.countryOptionTemplate}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row className="d-flex justify-content-center">
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>City</Label>
                                        <InputText
                                            value={this.state.address?.city}
                                            className={"form-control"}
                                            type="text"
                                            onChange={(e) => this.updateValue("city", e.target.value)}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>State</Label>
                                        <InputText
                                            value={this.state.address?.stateProvinceCounty}
                                            className={"form-control"}
                                            type="text"
                                            onChange={(e) => this.updateValue("stateProvinceCounty", e.target.value)}
                                        />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row className="d-flex justify-content-center">
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Zip Code</Label>
                                        <InputText
                                            value={this.state.address?.zipCode}
                                            className={"form-control"}
                                            type="text"
                                            onChange={(e) => this.updateValue("zipCode", e.target.value)}
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup></FormGroup>
                                </Col>
                            </Row>
                            <div className="flex justify-end px-5 py-4">
                                <Button label="Update" icon={<Check size={16} />} loading={this.state.isLoading} onClick={this.onSave} />
                            </div>
                        </Container>
            </Dialog>
        );
    }
}
